import React, { useState } from "react";
import "../../../Styles/Tpregistration.css";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ApiService from "../../../nhrc_apis/ApiService";

const { UserAPI } = ApiService;

const EventRegistration = () => {
  const navigate  = useNavigate();
  const { state } = useLocation();
  const eventData = state?.event;

  const [formData, setFormData] = useState({
    fullName:        "",
    mobile:          "",
    email:           "",
    location:        "",
    iam_a:           "",
    nhrcId:          "",
    companyname:     "",
    companylocation: "",
    updates:         false,
  });

  const [errors,       setErrors]       = useState({});
  const [touched,      setTouched]      = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── VALIDATE SINGLE FIELD ────────────────────────────────
  const validateField = (name, value) => {
    let error = "";

    switch (name) {

      case "fullName":
        if (!value || !value.trim()) {
          error = "Full name is required.";
        } else if (value.trim().length < 3) {
          error = "Full name must be at least 3 characters.";
        } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          error = "Full name must contain only letters and spaces.";
        }
        break;

      case "mobile":
        if (!value || !value.trim()) {
          error = "Phone number is required.";
        } else if (!/^[0-9]{10}$/.test(value.trim())) {
          error = "Phone number must be exactly 10 digits.";
        }
        break;

      case "email":
        if (!value || !value.trim()) {
          error = "Email is required.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          error = "Enter a valid email address (e.g. name@example.com).";
        }
        break;

      case "location":
        if (!value || !value.trim()) {
          error = "Location is required.";
        } else if (value.trim().length < 2) {
          error = "Location must be at least 2 characters.";
        }
        break;

      case "iam_a":
        if (!value || !value.trim()) {
          error = "This field is required.";
        } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          error = "This field must contain only letters and spaces.";
        }
        break;

      case "nhrcId":
        if (!value || !value.trim()) {
          error = "NHRC ID is required.";
        } else if (value.trim().length < 3) {
          error = "NHRC ID must be at least 3 characters.";
        }
        break;

      case "companyname":
        if (!value || !value.trim()) {
          error = "Company name is required.";
        } else if (value.trim().length < 2) {
          error = "Company name must be at least 2 characters.";
        }
        break;

      case "companylocation":
        if (!value || !value.trim()) {
          error = "Company location is required.";
        } else if (value.trim().length < 2) {
          error = "Company location must be at least 2 characters.";
        }
        break;

      default:
        break;
    }

    return error;
  };

  // ─── VALIDATE ALL FIELDS ──────────────────────────────────
  const validateAll = () => {
    const fields = [
      "fullName", "mobile", "email", "location",
      "iam_a", "nhrcId", "companyname", "companylocation",
    ];

    const newErrors  = {};
    const newTouched = {};

    fields.forEach((field) => {
      newTouched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    setTouched(newTouched);
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // ─── HANDLE CHANGE ────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData({ ...formData, [name]: newValue });

    if (name !== "updates") {
      const error = validateField(name, newValue);
      setErrors((prev)  => ({ ...prev, [name]: error }));
      setTouched((prev) => ({ ...prev, [name]: true  }));
    }
  };

  // ─── HANDLE SUBMIT ────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const isValid = validateAll();

    if (!isValid) {
      toast.error("Please fill all required fields correctly.", { autoClose: 2000 });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        event_id:         eventData?.id,
        job_fair_id:      null,
        full_name:        formData.fullName.trim(),
        phone:            formData.mobile.trim(),
        location:         formData.location.trim(),
        iam_a:            formData.iam_a.trim(),
        nhrc_id:          formData.nhrcId.trim(),
        receive_updates:  formData.updates,
        college_name:     null,
        year_of_passout:  null,
        company_name:     formData.companyname.trim(),
        company_location: formData.companylocation.trim(),
      };

      console.log("REQUEST PAYLOAD:", payload);

      const response = await UserAPI.registerUser(payload);

      console.log("REGISTER SUCCESS", response.data);

      toast.success("Registered Successfully!", { autoClose: 2000 });

      setTimeout(() => {
        navigate("/hrdashboard/Hrevents");
      }, 2000);

    } catch (error) {
      console.log("REGISTER ERROR", error?.response?.data);

      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;

      if (status === 422 && Array.isArray(detail)) {
        const firstError = detail[0];
        const field = firstError?.loc?.[1] || "field";
        const msg   = firstError?.msg       || "Validation error";
        toast.error(`Validation error — ${field}: ${msg}`, { autoClose: 3500 });

      } else if (status === 401) {
        toast.error("Session expired. Please login again.", { autoClose: 2500 });
        setTimeout(() => navigate("/login"), 2600);

      } else if (status === 409) {
        toast.error("You have already registered for this event!", { autoClose: 2500 });

      } else if (typeof detail === "string") {
        toast.error(detail, { autoClose: 2500 });

      } else {
        toast.error(
          error?.response?.data?.message || "Registration Failed. Please try again.",
          { autoClose: 2500 }
        );
      }

    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── RENDER ───────────────────────────────────────────────
  return (
    <>
      <div className="eventreg-wrapper mt-0">
        <h2 className="eventreg-title">REGISTER FORM</h2>

        {eventData && (
          <div className="eventreg-training-info">
            <h3 className="eventreg-training-title" style={{ color: "#d8d83fff" }}>
              {eventData.title}
            </h3>
            <p className="eventreg-training-schedule">
              {eventData.schedule} | {eventData.title}
            </p>
          </div>
        )}

        <form className="eventreg-card" onSubmit={handleSubmit} noValidate>
          <div className="eventreg-grid">

            {/* Full Name */}
            <div className="eventreg-field">
              <label>Full Name*</label>
              <input
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Enter your full name"
                className={errors.fullName ? "input-error" : ""}
              />
              {errors.fullName && <span className="error-text">{errors.fullName}</span>}
            </div>

            {/* Phone Number */}
            <div className="eventreg-field">
              <label>Phone Number*</label>
              <input
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="Enter 10-digit phone number"
                maxLength={10}
                className={errors.mobile ? "input-error" : ""}
              />
              {errors.mobile && <span className="error-text">{errors.mobile}</span>}
            </div>

            {/* Email */}
            <div className="eventreg-field">
              <label>Email Id*</label>
              <input
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                className={errors.email ? "input-error" : ""}
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            {/* Location */}
            <div className="eventreg-field">
              <label>Location*</label>
              <input
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Enter your location"
                className={errors.location ? "input-error" : ""}
              />
              {errors.location && <span className="error-text">{errors.location}</span>}
            </div>

            {/* I am a */}
            <div className="eventreg-field">
              <label>I am a*</label>
              <input
                name="iam_a"
                value={formData.iam_a}
                onChange={handleChange}
                placeholder="e.g. Student, Professional"
                className={errors.iam_a ? "input-error" : ""}
              />
              {errors.iam_a && <span className="error-text">{errors.iam_a}</span>}
            </div>

            {/* NHRC ID */}
            <div className="eventreg-field">
              <label>NHRC ID*</label>
              <input
                name="nhrcId"
                value={formData.nhrcId}
                onChange={handleChange}
                placeholder="Enter your NHRC ID"
                className={errors.nhrcId ? "input-error" : ""}
              />
              {errors.nhrcId && <span className="error-text">{errors.nhrcId}</span>}
            </div>

            {/* Company Name */}
            <div className="eventreg-field">
              <label>Company Name*</label>
              <input
                name="companyname"
                value={formData.companyname}
                onChange={handleChange}
                placeholder="Enter your company name"
                className={errors.companyname ? "input-error" : ""}
              />
              {errors.companyname && <span className="error-text">{errors.companyname}</span>}
            </div>

            {/* Company Location */}
            <div className="eventreg-field">
              <label>Company Location*</label>
              <input
                name="companylocation"
                value={formData.companylocation}
                onChange={handleChange}
                placeholder="Enter company location"
                className={errors.companylocation ? "input-error" : ""}
              />
              {errors.companylocation && <span className="error-text">{errors.companylocation}</span>}
            </div>

            {/* Checkbox */}
            <div className="eventreg-checkbox">
              <input
                type="checkbox"
                name="updates"
                id="updates"
                checked={formData.updates}
                onChange={handleChange}
              />
              <label htmlFor="updates">
                Would you like to receive updates about future events and
                professional opportunities? *
              </label>
            </div>

          </div>

          <button
            type="submit"
            className="eventreg-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "SUBMIT"}
          </button>

        </form>
      </div>

      <ToastContainer position="top-center" />
    </>
  );
};

export default EventRegistration;